/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { IconButton, Typography2 } from '@coursera/cds-core';

import { useCarouselContext } from 'bundles/cds-labs/components/Carousel/Carousel';

import _t from 'i18n!nls/cds-labs';

const styles = {
  navigation: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-200);

    /* Prevents text selection on adjacent elements when clicking rapidly */
    user-select: none;
  `,
  pageLabel: css`
    white-space: nowrap;
    user-select: none;
  `,
  pageLabelHidden: css`
    visibility: hidden;
  `,
  navigationCompact: css`
    gap: var(--cds-spacing-100);
  `,
  overlayPrev: css`
    position: absolute;
    left: 0;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 1;
    user-select: none;
    box-shadow:
      0 6px 12px 4px var(--cds-color-darken-100),
      0 0 4px 0 var(--cds-color-darken-100);
  `,
  overlayNext: css`
    position: absolute;
    right: 0;
    top: 50%;
    transform: translate(50%, -50%);
    z-index: 1;
    user-select: none;
    box-shadow:
      0 6px 12px 4px var(--cds-color-darken-100),
      0 0 4px 0 var(--cds-color-darken-100);
  `,
};

type Props = {
  className?: string;
  /** Overrides the navigationVariant from context. Use 'overlay' to force overlay, 'default' to force standard pagination. */
  navigationVariant?: 'overlay' | 'default';
  /**
   * Render only one overlay button. Used when splitting the overlay nav so that the Previous button
   * can be placed before the slides in the DOM and the Next button after, giving the correct semantic
   * tab order (Prev → slides → Next) while CSS keeps them visually at the carousel edges.
   */
  overlayButton?: 'prev' | 'next';
  /**
   * For the standard navigation variant: render only the specified button.
   * "prev" renders only the Previous button (for placement before slides in DOM).
   * "next" renders the page counter + Next button (for placement after slides in DOM).
   * Has no effect when navigationVariant is 'overlay'.
   */
  button?: 'prev' | 'next';
  disabled?: boolean;
  hidePageLabel?: boolean;
};

const CarouselNavigation = ({
  className,
  navigationVariant: navigationVariantProp,
  overlayButton,
  button,
  disabled = false,
  hidePageLabel = false,
}: Props) => {
  const {
    scrollPrev,
    scrollNext,
    canScrollPrev,
    canScrollNext,
    currentPage,
    totalPages,
    navigationVariant: navigationVariantCtx,
  } = useCarouselContext();

  const navigationVariant =
    navigationVariantProp === 'default' ? undefined : (navigationVariantProp ?? navigationVariantCtx);

  if (navigationVariant === 'overlay') {
    // When rendered as a split pair (overlayButton="prev" before slides, "next" after), each call
    // renders only one button. Visual position is set by CSS; DOM order gives the correct tab order:
    // Previous → slides → Next.
    if (overlayButton === 'prev') {
      return canScrollPrev ? (
        <IconButton
          css={styles.overlayPrev}
          variant="secondaryNeutral"
          shapeVariant="round"
          size="medium"
          intent="previous"
          disabled={disabled}
          onClick={(e) => scrollPrev(e.detail === 0)}
        />
      ) : null;
    }
    if (overlayButton === 'next') {
      return canScrollNext ? (
        <IconButton
          css={styles.overlayNext}
          variant="secondaryNeutral"
          shapeVariant="round"
          size="medium"
          intent="next"
          disabled={disabled}
          onClick={(e) => scrollNext(e.detail === 0)}
        />
      ) : null;
    }
    // Fallback: render both buttons together (Previous before Next for correct semantic order).
    return (
      <React.Fragment>
        {canScrollPrev && (
          <IconButton
            css={styles.overlayPrev}
            variant="secondaryNeutral"
            shapeVariant="round"
            size="medium"
            intent="previous"
            disabled={disabled}
            onClick={(e) => scrollPrev(e.detail === 0)}
          />
        )}
        {canScrollNext && (
          <IconButton
            css={styles.overlayNext}
            variant="secondaryNeutral"
            shapeVariant="round"
            size="medium"
            intent="next"
            disabled={disabled}
            onClick={(e) => scrollNext(e.detail === 0)}
          />
        )}
      </React.Fragment>
    );
  }

  if (totalPages <= 1) return null;

  const isDefaultNavigation = navigationVariantProp === 'default';
  const showPageLabel = !isDefaultNavigation;
  const isPrevDisabled = disabled || !canScrollPrev;
  const isNextDisabled = disabled || !canScrollNext;

  // Split rendering: "prev" renders only the Previous button; "next" renders the counter + Next button.
  // This allows consumers to place Prev before slides and Next after slides in the DOM
  // while CSS Grid keeps them visually adjacent in the header row.
  if (button === 'prev') {
    return (
      <IconButton
        variant="secondaryNeutral"
        shapeVariant="round"
        size="small"
        intent="previous"
        disabled={isPrevDisabled}
        onClick={(e) => scrollPrev(e.detail === 0)}
      />
    );
  }

  if (button === 'next') {
    return (
      <div css={[styles.navigation, isDefaultNavigation && styles.navigationCompact]} className={className}>
        {showPageLabel && (
          <Typography2
            variant="bodySecondary"
            component="span"
            color="supportText"
            css={[styles.pageLabel, hidePageLabel && styles.pageLabelHidden]}
            aria-hidden="true"
          >
            {_t('#{current} of #{total}', { current: currentPage, total: totalPages })}
          </Typography2>
        )}
        <IconButton
          variant="secondaryNeutral"
          shapeVariant="round"
          size="small"
          intent="next"
          disabled={isNextDisabled}
          onClick={(e) => scrollNext(e.detail === 0)}
          data-track-component="show_more"
        />
      </div>
    );
  }

  return (
    <div css={[styles.navigation, isDefaultNavigation && styles.navigationCompact]} className={className}>
      <IconButton
        variant="secondaryNeutral"
        shapeVariant="round"
        size="small"
        intent="previous"
        disabled={isPrevDisabled}
        onClick={(e) => scrollPrev(e.detail === 0)}
      />
      {showPageLabel && (
        <Typography2
          variant="bodySecondary"
          component="span"
          color="supportText"
          css={[styles.pageLabel, hidePageLabel && styles.pageLabelHidden]}
          aria-hidden="true"
        >
          {_t('#{current} of #{total}', { current: currentPage, total: totalPages })}
        </Typography2>
      )}
      <IconButton
        variant="secondaryNeutral"
        shapeVariant="round"
        size="small"
        intent="next"
        disabled={isNextDisabled}
        onClick={(e) => scrollNext(e.detail === 0)}
        data-track-component="show_more"
      />
    </div>
  );
};

export default CarouselNavigation;
